import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';

type ToastKind = 'success' | 'error' | 'info';

interface Toast {
  id: string;
  kind: ToastKind;
  title: string;
  description?: string;
}

interface ToastContextValue {
  show: (toast: Omit<Toast, 'id'>) => void;
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
  info: (title: string, description?: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const DURATION_MS = 4000;

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const remove = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const show = useCallback(
    (toast: Omit<Toast, 'id'>) => {
      const id = `toast-${Math.random().toString(36).slice(2, 10)}`;
      setToasts(prev => [...prev, { ...toast, id }]);
      window.setTimeout(() => remove(id), DURATION_MS);
    },
    [remove],
  );

  const value: ToastContextValue = {
    show,
    success: (title, description) => show({ kind: 'success', title, description }),
    error: (title, description) => show({ kind: 'error', title, description }),
    info: (title, description) => show({ kind: 'info', title, description }),
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed bottom-6 right-6 z-[60] flex flex-col gap-2 max-w-sm">
        {toasts.map(t => {
          const tokens =
            t.kind === 'success'
              ? { bg: 'bg-white', dot: 'bg-emerald-500', border: 'border-zinc-200' }
              : t.kind === 'error'
                ? { bg: 'bg-white', dot: 'bg-red-500', border: 'border-red-200' }
                : { bg: 'bg-white', dot: 'bg-blue-500', border: 'border-zinc-200' };
          return (
            <div
              key={t.id}
              role="status"
              className={`${tokens.bg} border ${tokens.border} rounded-md shadow-md px-4 py-3 flex items-start gap-3 animate-in fade-in slide-in-from-bottom-2`}
            >
              <span className={`mt-1.5 h-2 w-2 rounded-full ${tokens.dot} shrink-0`} aria-hidden />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-zinc-900">{t.title}</p>
                {t.description ? <p className="text-xs text-zinc-500 mt-0.5">{t.description}</p> : null}
              </div>
              <button
                type="button"
                onClick={() => remove(t.id)}
                className="text-zinc-400 hover:text-zinc-700 text-xs"
                aria-label="Fermer"
              >
                ×
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextValue => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast doit être utilisé à l'intérieur d'<ToastProvider>");
  return ctx;
};
